/**
 * Shared trial status display helpers for the Recordings table.
 *
 * - ``ready``: upload complete; user has not started Process (Ready pill).
 * - ``queued``: Process was clicked; GPU accepted and job is waiting (Queued 0% bar).
 * - ``processing`` with 0% and no pending click: stale API row → show as Ready.
 */

export function hasValidTrialProgress(progress) {
  const raw = Number(progress);
  return Number.isFinite(raw) && raw >= 0 && raw <= 100;
}

/** True when the worker has reported measurable progress (show progress bar). */
export function hasActiveTrialProgress(progress) {
  const raw = Number(progress);
  return Number.isFinite(raw) && raw >= 1 && raw <= 100;
}

export function getTrialStageLabel(progress, status, stage) {
  if (status === 'uploading') return 'Uploading';
  const raw = Number(progress);
  const clamped = hasValidTrialProgress(raw) ? Math.max(0, Math.min(100, raw)) : 0;
  // While the worker is processing, the bar shows the pipeline stage
  // (for example floor_detect). That name is not a failure reason.
  if (status === 'processing' && clamped >= 1) {
    const name = typeof stage === 'string' ? stage.trim() : '';
    if (name) return name;
  }
  if (clamped >= 95 && clamped < 100) return 'Finalizing';
  if (clamped >= 1) return 'Processing';
  return 'Queued';
}

/**
 * Whether the row should render the blue progress bar (Queued 0% or active processing).
 */
export function shouldShowTrialProgressBar(status, progress, options = {}) {
  if (status === 'queued' || status === 'uploading') return true;
  if (status !== 'processing') return false;
  return options.pendingProcess || hasActiveTrialProgress(progress);
}

/**
 * @param {string|undefined} status - Raw trial status from API or local state
 * @param {number|null|undefined} progress
 * @param {{ pendingProcess?: boolean }} [options] - UI optimistically started Process
 */
export function getEffectiveTrialStatus(status, progress, options = {}) {
  if (status === 'queued') return 'queued';
  if (status !== 'processing') return status;
  if (options.pendingProcess || hasActiveTrialProgress(progress)) return 'processing';
  return 'ready';
}

/** Normalize list payloads: phantom processing (0%) → ready; keep queued as-is. */
export function normalizeTrialProcessingStatus(trial) {
  if (!trial || typeof trial !== 'object') return trial;
  if (trial.status === 'queued') return trial;
  if (trial.status !== 'processing') return trial;
  if (hasActiveTrialProgress(trial.progress)) return trial;
  return { ...trial, status: 'ready', progress: null };
}

/** Trial IDs that need status polling (queued or active processing). */
export function trialNeedsStatusPolling(status) {
  return status === 'queued' || status === 'processing' || status === 'uploading';
}

const TRIAL_STATUS_ORDER = { ready: 0, uploading: 1, queued: 2, processing: 3, complete: 4, failed: 4 };

/** Coerce API progress (number or numeric string) to 0–100, or null when invalid/absent. */
export function parseTrialProgress(progress) {
  if (progress == null || progress === '') return null;
  const raw = Number(progress);
  if (!Number.isFinite(raw) || raw < 0 || raw > 100) return null;
  return raw;
}

/**
 * 0–100 from camera file counts on an uploading status row.
 * `uploaded` / `total` come from GET /a/get-trials-status (and the single-trial poll).
 */
function uploadCountPercent(match) {
  if (match.uploaded == null || match.uploaded === '' || match.total == null || match.total === '') {
    return null;
  }
  const uploaded = Number(match.uploaded);
  const total = Number(match.total);
  if (!Number.isFinite(uploaded) || !Number.isFinite(total) || total <= 0 || uploaded < 0) return null;
  const pct = Math.round((uploaded / total) * 100);
  if (pct > 100) return 100;
  return pct;
}

/**
 * Progress the recordings bar should show for one status-poll row.
 * Processing still uses `progress` only. While `status` is `uploading`, file
 * counts can fill the bar when `progress` is still 0.
 */
export function resolvePolledTrialProgress(match) {
  if (!match || typeof match !== 'object') return null;
  const direct = parseTrialProgress(match.progress);
  if (match.status !== 'uploading') return direct;
  const fromCounts = uploadCountPercent(match);
  if (direct == null) return fromCounts;
  if (fromCounts == null) return direct;
  return Math.max(direct, fromCounts);
}

/**
 * Merge one batch/single-trial poll payload into a recordings table row.
 * Status only advances in TRIAL_STATUS_ORDER. Progress only moves forward
 * within the current phase — a stale earlier phase (upload finished at 100%
 * while Process was just clicked) must not raise the bar.
 */
export function mergeTrialPollUpdate(row, match) {
  if (!match) return row;
  const incomingStatus = match.status;
  const currentStatus = row.status;
  let nextStatus = currentStatus;
  let stalePhase = false;
  if (incomingStatus) {
    const currentRank = TRIAL_STATUS_ORDER[currentStatus] ?? 0;
    const incomingRank = TRIAL_STATUS_ORDER[incomingStatus] ?? 0;
    const incomingKnown = Object.prototype.hasOwnProperty.call(TRIAL_STATUS_ORDER, incomingStatus);
    // Upload finishes by becoming ready, which ranks below uploading.
    const uploadFinished = currentStatus === 'uploading' && incomingStatus === 'ready';
    stalePhase = incomingKnown && !uploadFinished && incomingRank < currentRank;
    if (uploadFinished || incomingRank >= currentRank) nextStatus = incomingStatus;
  }
  const incomingProgress = resolvePolledTrialProgress(match);
  const currentProgress = parseTrialProgress(row.progress);
  const nextProgress = stalePhase
    ? currentProgress
    : incomingProgress != null && currentProgress != null
      ? Math.max(currentProgress, incomingProgress)
      : incomingProgress ?? currentProgress ?? null;
  const next = {
    ...row,
    status: nextStatus,
    progress: nextProgress,
    issue: match.issue ?? row.issue,
  };
  // Stage is the worker phase label. It must not be copied into `issue`.
  if (!stalePhase && Object.prototype.hasOwnProperty.call(match, 'stage')) {
    next.stage = match.stage;
  }
  return next;
}

/** Clear optimistic "Process" pending state once the worker reports real progress. */
export function shouldClearPendingProcessPoll(pollRow) {
  if (!pollRow) return false;
  // A still-ready row can carry upload progress of 100. That is not the worker.
  if (pollRow.status === 'ready' || pollRow.status === 'uploading') return false;
  if (pollRow.status === 'queued') return true;
  const progress = parseTrialProgress(pollRow.progress);
  return progress != null && progress >= 1;
}
